import React, { useCallback, useMemo, useState } from 'react';
import styles from './hsaResetPassword.module.scss';
import ParentAuthPageLayout from '../../../components/auth-page-layout/HsaAuthPageLayout';
import { TextInput, TextInputValue } from '../../../components/text-input/TextInput';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { NavLink } from 'react-router-dom';
import { validatePassword } from '../../../utils/validation';
import Button from 'src/components/button/Button';
import { useHeaResetPasswordMutation } from '@src/api/graphql-codegen';

type Props = {};

function ForgotPasswordConfirm(props: Props) {
  const token = useQueryParams().get('token');

  const [reset, { loading, error }] = useHeaResetPasswordMutation({
    onCompleted: () => {
      setCompleted(true);
    },
    onError: () => {
      setCompleted(true);
    }
  });

  const [clientError, setClientError] = useState<undefined | string>();
  const [completed, setCompleted] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChange = useCallback((setter) => (value: TextInputValue) => {
    setClientError(undefined);
    setter(value);
  }, []);

  const handleSubmit = useCallback(() => {
    const error = validatePassword(newPassword);
    if (error) {
      return setClientError(error);
    }

    if (newPassword !== confirmPassword) {
      return setClientError('The password and the confirm password should match.');
    }

    if (token) {
      reset({ variables: { input: { token, password: newPassword } } });
    }
  }, [newPassword, confirmPassword, reset, token]);

  const completedContent = useMemo(() => () => {
    if (!completed) return null;
    if (error) {
      return (
        <div className={styles.confirm}>
          <div className={styles.confirmErrorTitle}>Error!</div>
          <div className={styles.confirmSubtitle}>{error.message}</div>
          <div className={styles.footer}>
            Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.confirm}>
        <div className={styles.confirmTitle}>Your password has been set successfully!</div>
        <div/>
        <div className={styles.footer}>
          Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
        </div>
      </div>
    )
  }, [completed, error]);

  return (
    <ParentAuthPageLayout backToLogin={true} title={'Reset your password'}>
      {completed ? completedContent :
        <>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'New password'} label={'New password*'}
                     id={'password'}
                     value={newPassword} onChange={handleChange(setNewPassword)}/>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'Confirm password'}
                     label={'Confirm password*'}
                     id={'confirmPassword'} value={confirmPassword} onChange={handleChange(setConfirmPassword)}/>
          {!!clientError && <div className={styles.error}>{clientError}</div>}
          <Button disabled={loading} className={styles.submit} onClick={handleSubmit} label={'Reset password'} />
        </>
      }
    </ParentAuthPageLayout>
  );
}

export default ForgotPasswordConfirm;
